import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SameWidthButton} from '../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {Dropdown, DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {CalendarEventVersionReminder} from '../../../models/api/calendarEventVersionModel';
import {CalendarModel} from '../../../models/api/calendarModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {DefaultTeammateAccessRolesEnum} from '../../../models/api/companyPreferencesModel';
import {CalendarVersionPatch} from '../../../store/data/calendarVersions/calendarVersionsActions';
import {PaletteColorPicker} from '../../common/color/paletteColorPicker';
import {DialogButtonGroup} from '../../common/dialogs/dialogButtonGroup';
import {DialogContent} from '../../common/dialogs/dialogContent';
import {CalendarRemindersEditor} from '../reminders/calendarRemindersEditor';
import {CalendarTeammateAccessRoleDropdownBox} from './calendarTeammateAccessRoleDropdownBox';

/*
 * Props.
 */

interface EditCalendarDropdownProps extends DropdownProps {
  calendar: CalendarModel;
  calendarVersion: CalendarVersionModel;
  defaultTeammateAccessRole: DefaultTeammateAccessRolesEnum;
  onSaveClick: HandlerOf<CalendarVersionPatch>;
  onBackButtonClick: Handler;
}

/*
 * Styles.
 */

const StyledDialogContentDiv = styled.div`
  flex-grow: 1;

  display: flex;
  flex-direction: column;
`;

const StyledDialogMainContentDiv = styled.div`
  flex: 1;
`;

const StyledTitleDiv = styled.div`
  margin-top: 8px;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledColorPickerDiv = styled.div`
  margin: 12px 0 18px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  grid-column-gap: 5px;
`;

/*
 * Component.
 */

export const EditCalendarDropdown: FC<EditCalendarDropdownProps> = (props) => {
  const {calendar, calendarVersion, onSaveClick, onRequestClose} = props;
  const {defaultTeammateAccessRole} = props;

  const [name, setName] = useState(calendarVersion.nameOverride || calendar.name);
  const [color, setColor] = useState(calendarVersion.color);
  const [reminders, setReminders] = useState(calendarVersion.reminders);
  const [teammateAccessRole, setTeammateAccessRole] = useState(calendar.teammateAccessRole);

  const onSaveButtonClick = () => {
    onSaveClick({
      nameOverride: name,
      color,
      reminders,
      calendar: {
        teammateAccessRole:
          teammateAccessRole !== calendar.teammateAccessRole ? teammateAccessRole : undefined,
      },
    });
  };

  return (
    <Dropdown {...props} width={300} hasBackground>
      <DropdownHeader
        title={<Trans id="edit-calendar-dropdown-title">Edit calendar</Trans>}
        leftIcon={
          <IconButton
            iconName="chevronLeftSmall"
            label={<Trans id="edit-calendar-dropdown-back">Back</Trans>}
            onClick={props.onBackButtonClick}
          />
        }
      />
      <StyledDialogContentDiv>
        <StyledDialogMainContentDiv>
          <DialogContent>
            {renderNameInput(name, setName)}
            {renderColorPicker(color, setColor)}
            <ReminderSettings reminders={reminders} onRemindersChange={setReminders} />
            {calendarVersion.accessRole === CalendarVersionAccessRolesEnum.OWNER && (
              <CalendarTeammateAccessRoleDropdownBox
                defaultTeammateAccessRole={defaultTeammateAccessRole}
                teammateAccessRole={teammateAccessRole}
                onTeammateAccessRoleChange={setTeammateAccessRole}
              />
            )}
          </DialogContent>
        </StyledDialogMainContentDiv>
        <BottomButtons
          isSaveDisabled={!name}
          onSaveClick={onSaveButtonClick}
          onCancelButtonClick={onRequestClose}
        />
      </StyledDialogContentDiv>
    </Dropdown>
  );
};

function renderNameInput(name: string, setName: HandlerOf<string>) {
  return (
    <TextBox
      label={<Trans id="edit-calendar-dropdown-name">Name</Trans>}
      value={name}
      onChange={setName}
      shouldAutoFocus
    />
  );
}

function renderColorPicker(color: PaletteColorsEnum, onCalendarColorSelect: HandlerOf<PaletteColorsEnum>) {
  return (
    <>
      <StyledTitleDiv>
        <Trans id="edit-calendar-dropdown-color">Color</Trans>
      </StyledTitleDiv>
      <StyledColorPickerDiv>
        <PaletteColorPicker paletteColor={color} onColorSelect={onCalendarColorSelect} />
      </StyledColorPickerDiv>
    </>
  );
}

/*
 * Reminder settings.
 */

interface ReminderSettingsProps {
  reminders: ReadonlyArray<CalendarEventVersionReminder>;
  onRemindersChange: HandlerOf<ReadonlyArray<CalendarEventVersionReminder>>;
}

const ReminderSettings: FC<ReminderSettingsProps> = (props) => {
  const {reminders, onRemindersChange} = props;
  return (
    <>
      <StyledTitleDiv>
        <Trans id="edit-calendar-dropdown-notifications">Notifications</Trans>
      </StyledTitleDiv>
      <StyledColorPickerDiv>
        <CalendarRemindersEditor reminders={reminders} onRemindersChange={onRemindersChange} />
      </StyledColorPickerDiv>
    </>
  );
};

/*
 * Bottom buttons.
 */

interface BottomButtonsProps {
  isSaveDisabled: boolean;
  onSaveClick: Handler;
  onCancelButtonClick: Handler | undefined;
}

const BottomButtons: FC<BottomButtonsProps> = (props) => {
  const {onCancelButtonClick, onSaveClick, isSaveDisabled} = props;
  return (
    <DialogButtonGroup>
      <ButtonGroupSpacer />
      <SameWidthButton onClick={onCancelButtonClick}>
        <Trans id="dropdown-cancel">Cancel</Trans>
      </SameWidthButton>
      <SameWidthButton onClick={onSaveClick} variant={ButtonVariantsEnum.PRIMARY} isDisabled={isSaveDisabled}>
        <Trans id="dropdown-save">Save</Trans>
      </SameWidthButton>
    </DialogButtonGroup>
  );
};
